import {
  ResponseDatetimeSubTypeEnum,
  ResponseTimeMeasurementRuleTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { NodeId } from '~/domain/report/model/report/node/node'
import type { QuestionNode } from '~/domain/report/model/report/node/questionNode/questionNode'
import type { TimeMeasurementAnswer } from '~/domain/report/model/report/node/questionNode/timeMeasurementQuestionNode/timeMeasurementAnswer'
import type {
  TimeMeasurementFormat,
  TimeMeasurementQuestion,
} from '~/domain/report/model/report/node/questionNode/timeMeasurementQuestionNode/timeMeasurementQuestion'
import type { TimeMeasurementQuestionNode } from '~/domain/report/model/report/node/questionNode/timeMeasurementQuestionNode/timeMeasurementQuestionNode'
import type { TimeMeasurementRule } from '~/domain/report/model/report/node/questionNode/timeMeasurementQuestionNode/timeMeasurementRule'
import { isNullish } from '~/utils/isNullish'
import { ApiToModelError } from '../error'
import { selectSiblingNodeIds } from './selectSiblingNodeIds'
import type { ConversionContext } from './types'
import type {
  ReportNodeSchema,
  ResponseTimeMeasurement,
} from '@ulysses-inc/harami_api_client'

export const convertTimeMeasurementQuestionNode = (
  context: ConversionContext,
): QuestionNode => {
  const {
    question,
    base: { node, questionNodeBase, questionBase, deviateProperty, recordedAt },
  } = context

  const responseTimeMeasurement = extractResponseTimeMeasurement(node)

  const timeMeasurementQuestion: TimeMeasurementQuestion = {
    ...questionBase,
    ...deviateProperty,
    type: 'timeMeasurement',
    format: convertToFormat(node, responseTimeMeasurement),
    startQuestionNodeId: resolveReferencedNodeId(
      context,
      responseTimeMeasurement.startQuestionNodeUUID,
    ),
    endQuestionNodeId: resolveReferencedNodeId(
      context,
      responseTimeMeasurement.endQuestionNodeUUID,
    ),
    rule: convertToRule(node, responseTimeMeasurement),
  }

  const timeMeasurementValue = question.responseAnswer?.timeMeasurementValue
  const timeMeasurementAnswer: TimeMeasurementAnswer | undefined =
    timeMeasurementValue === undefined
      ? undefined
      : {
          type: 'timeMeasurement',
          value: timeMeasurementValue.value,
          isInvalid: timeMeasurementValue.isInvalid,
          recordedAt,
        }

  const questionNode: TimeMeasurementQuestionNode = {
    ...questionNodeBase,
    questionType: 'timeMeasurement',
    question: timeMeasurementQuestion,
    answer: timeMeasurementAnswer,
  }

  return questionNode
}

/**
 * 計測に使う日時質問(開始・終了)のノード id を uuid から解決する。
 * 仕様より、参照先は同一の親に属する質問のみである
 */
const resolveReferencedNodeId = (
  context: ConversionContext,
  questionNodeUUID: string,
): NodeId => {
  const {
    wholeContext: { nodeDict },
    base: { node },
  } = context

  const siblingNodeIds = selectSiblingNodeIds(context)
  const referencedNodeId = siblingNodeIds.find(
    siblingNodeId => nodeDict[siblingNodeId]?.uuid === questionNodeUUID,
  )
  if (isNullish(referencedNodeId)) {
    throw new ApiToModelError(
      `Referenced question node of time measurement is not defined.`,
      { node, questionNodeUUID },
    )
  }
  return referencedNodeId
}

const convertToFormat = (
  node: ReportNodeSchema,
  responseTimeMeasurement: ResponseTimeMeasurement,
): TimeMeasurementFormat => {
  switch (responseTimeMeasurement.subType) {
    case ResponseDatetimeSubTypeEnum.DATETIME:
      return 'dateTime'
    case ResponseDatetimeSubTypeEnum.TIME:
      return 'time'
    default:
      // 仕様より、計測に使う日時質問のフォーマットが日付(date)になることはない
      throw new ApiToModelError('Unsupported ResponseTimeMeasurement.subType', {
        node,
      })
  }
}

const convertToRule = (
  node: ReportNodeSchema,
  responseTimeMeasurement: ResponseTimeMeasurement,
): TimeMeasurementRule | undefined => {
  const rule = responseTimeMeasurement.rule
  if (isNullish(rule)) {
    return undefined
  }

  switch (rule.type) {
    case ResponseTimeMeasurementRuleTypeEnum.LESS_THAN_OR_EQUAL:
      return { type: 'le', minutes: rule.value }
    case ResponseTimeMeasurementRuleTypeEnum.GREATER_THAN_OR_EQUAL:
      return { type: 'ge', minutes: rule.value }
    default:
      throw new ApiToModelError(
        'Unsupported ResponseTimeMeasurementRule.type',
        { node },
      )
  }
}

const extractResponseTimeMeasurement = (
  node: ReportNodeSchema,
): ResponseTimeMeasurement => {
  const responseTimeMeasurement = node.question?.responseTimeMeasurements?.[0]
  if (isNullish(responseTimeMeasurement)) {
    throw new ApiToModelError(
      'responseTimeMeasurement is not defined in node',
      { node },
    )
  }
  return responseTimeMeasurement
}
